import { Button } from "@shared-ui/button";
import {
	Card,
	CardContent,
	CardDescription,
	CardHeader,
	CardTitle,
} from "@shared-ui/card";
import { Input } from "@shared-ui/input";
import { Label } from "@shared-ui/label";
import {
	Select,
	SelectContent,
	SelectGroup,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from "@shared-ui/select";
import { Skeleton } from "@shared-ui/skeleton";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useRef, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { EmptyState } from "@/components/ui/empty-state";
import { notify } from "@/components/ui/notify";
import { StatusBadge } from "@/components/ui/status-badge";
import { DataTable } from "@/features/catalog/components/data-table";
import {
	SourceDialog,
	type SourceDialogMode,
} from "@/features/catalog/components/source-dialog";
import {
	IMPORT_RUNS_PAGE,
	type ImportRun,
	listImportRuns,
	listImportSources,
	uploadImportFile,
} from "@/features/catalog/services/import-api.service";
import { apiErrorMessage } from "@/lib/api-error";
import { buildImportTemplateCsv } from "../lib/import-template.js";

/**
 * The imports screen: what has been loaded, and how to load the next one.
 *
 * ONE SCREEN FOR BOTH, deliberately. An operator arriving here is doing one of
 * two things -- starting a new import, or going back to one they started -- and
 * splitting them across two routes means the second thing needs a link from the
 * first. The runs list IS the history, so it belongs beside the form that adds
 * to it.
 *
 * The COLUMN MAPPING is not here. It needs the file's headers, which nothing
 * knows until the file has been read, so it lives on the run screen where the
 * headers exist. Staging first and mapping second is also what makes a mapping
 * correctable: the rows are already captured, so a wrong mapping is re-run
 * rather than re-uploaded.
 */

/**
 * What the file picker offers.
 *
 * MIRRORS THE ROUTE'S multer filter, which now derives itself from the
 * extractor registry -- so HTML is here because the JSON-LD extractor reads a
 * saved brand product page, not only because a CSV might arrive mislabelled.
 * `import-contract.test.ts` asserts every type the server accepts appears
 * here, rather than a comment claiming it. That is one direction only: an
 * extension or a stale type left in this list is not caught.
 *
 * A picker offering a format the server refuses is an invitation to fail; one
 * omitting a format the server accepts refuses a valid file for a reason the
 * operator cannot see. Extensions are here as well as mime types because macOS
 * reports some CSVs with no type at all.
 */
const ACCEPTED_FILES =
	".csv,.html,text/csv,text/plain,text/html,application/xhtml+xml,application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet";

/**
 * Whether a DROPPED file is one the route would accept.
 *
 * Checked against `ACCEPTED_FILES` rather than a second list, because the native
 * input's `accept` is that constant and a drop must not accept more than the
 * picker does. Extension AND type: macOS reports some CSVs with
 * no type at all, which is the same reason the constant carries both.
 */
function isAcceptedFile(candidate: File): boolean {
	const allowed = ACCEPTED_FILES.split(",");
	const name = candidate.name.toLowerCase();
	return allowed.some((one) =>
		one.startsWith(".") ? name.endsWith(one) : candidate.type === one,
	);
}

function counters(run: ImportRun): string {
	/*
	 * The five buckets, and only the ones that are non-zero.
	 *
	 * A row of five zeroes on a staged run tells an operator nothing and makes
	 * the one number that matters harder to find. A run nobody has resolved has
	 * all five at zero, which is why the total is always shown.
	 */
	const parts: string[] = [];
	if (run.rowsNew) parts.push(`${run.rowsNew} new`);
	if (run.rowsChanged) parts.push(`${run.rowsChanged} changed`);
	if (run.rowsUnchanged) parts.push(`${run.rowsUnchanged} unchanged`);
	if (run.rowsUnmatched) parts.push(`${run.rowsUnmatched} to review`);
	if (run.rowsErrored) parts.push(`${run.rowsErrored} errored`);
	return parts.length > 0 ? parts.join(", ") : "not resolved yet";
}

export function CatalogImportsPage() {
	const navigate = useNavigate();
	const queryClient = useQueryClient();
	const fileInput = useRef<HTMLInputElement>(null);

	const [sourceId, setSourceId] = useState("");
	const [file, setFile] = useState<File | null>(null);
	/** Nested enters minus leaves; see the drop zone's own comment. */
	const [dragDepth, setDragDepth] = useState(0);
	const [dropError, setDropError] = useState<string | null>(null);
	const [effectiveFrom, setEffectiveFrom] = useState("");
	const [actionError, setActionError] = useState<string | null>(null);
	/** Open when the operator creates a source without leaving this screen. */
	const [sourceDialog, setSourceDialog] = useState<SourceDialogMode | null>(
		null,
	);

	const sources = useQuery({
		queryKey: ["catalog-import-sources"],
		queryFn: listImportSources,
	});

	/*
	 * `?status=waiting` is the dashboard's "Imports waiting" list in full:
	 * staged or resolved runs only. Kept in the URL so that link lands here
	 * already filtered.
	 */
	const [searchParams, setSearchParams] = useSearchParams();
	const waitingOnly = searchParams.get("status") === "waiting";
	// Keyed on the filter, so a page into one list never carries into the
	// other -- including when the dashboard's link changes the filter.
	const [page, setPage] = useState({ waitingOnly, offset: 0 });
	const offset = page.waitingOnly === waitingOnly ? page.offset : 0;
	const setOffset = (next: number) => setPage({ waitingOnly, offset: next });
	const runs = useQuery({
		queryKey: ["catalog-import-runs", { waitingOnly, offset }],
		queryFn: () => listImportRuns({ waiting: waitingOnly, offset }),
	});

	const upload = useMutation({
		mutationFn: () => {
			if (!file) throw new Error("Choose a file first");
			return uploadImportFile({
				sourceId,
				file,
				effectiveFrom: effectiveFrom || undefined,
			});
		},
		onSuccess: (result) => {
			queryClient.invalidateQueries({ queryKey: ["catalog-import-runs"] });
			setFile(null);
			if (fileInput.current) fileInput.current.value = "";
			/*
			 * A re-upload of a byte-identical file RUNS rather than being refused
			 * -- V3 needs the second run to report zero changes -- so the only
			 * place an operator can learn they have done this before is here.
			 */
			if (result.duplicateOfRunId) {
				notify.partly("This file has been imported before", {
					description: "The new run will report what has changed since.",
				});
			} else {
				notify.done(`Staged ${result.run.rowsTotal} rows`);
			}
			navigate(`/catalog/imports/${result.run.id}`);
		},
		/*
		 * THE BANNER, AND NO TOAST -- the same split `product-editor.tsx` settled on.
		 *
		 * `api-client.ts` toasts the server's message for every failure EXCEPT a 401,
		 * so a screen that toasts too shows two. But removing the handler outright,
		 * which is what the previous commit did, left a 401 showing nothing at all:
		 * no toast, no banner, no navigation on two of the three 401 branches. An
		 * operator's session expiring mid-commit looked exactly like a click that did
		 * not register.
		 *
		 * So the handler comes back and sets state instead of toasting. On a
		 * non-401 the interceptor's toast and this banner say the same thing in two
		 * places, which is the right way round: one of them is transient and the
		 * other survives long enough to read.
		 */
		onError: (error) =>
			setActionError(apiErrorMessage(error, "Could not stage that file.")),
	});

	const activeSources = (sources.data ?? []).filter((s) => s.isActive);
	const canUpload = Boolean(sourceId && file) && !upload.isPending;

	/*
	 * Built here rather than served as a static file.
	 *
	 * The columns come from the same array the mapping picker offers, which
	 * `import-contract.test.ts` already pins to the server's `IMPORT_FIELD_KINDS`.
	 * A checked-in CSV would be a second copy of that list with nothing keeping
	 * the two in step -- and a template that offers a column the pipeline cannot
	 * write is worse than no template, because the operator only finds out after
	 * filling in two thousand rows.
	 */
	const downloadTemplate = () => {
		// The BOM is for Excel, which otherwise reads UTF-8 as the local codepage
		// and mangles any non-ASCII character in a product name. Prepended to the
		// bytes, not merely declared in the mime type -- Excel does not read the
		// latter.
		const blob = new Blob(["\uFEFF", buildImportTemplateCsv()], {
			type: "text/csv;charset=utf-8;",
		});
		const url = URL.createObjectURL(blob);
		const link = document.createElement("a");
		link.href = url;
		link.download = "catalog-import-template.csv";
		link.click();
		URL.revokeObjectURL(url);
	};

	return (
		<div className="grid gap-4">
			<Card>
				<CardHeader>
					<CardTitle>Import a price list</CardTitle>
					<CardDescription>
						The file is captured first and interpreted second, so a wrong column
						mapping is corrected on the next screen rather than by uploading
						again. {/*
						 * A link rather than a fifth sidebar entry. Registering a source
						 * is setup done on the way to an import -- and this is where an
						 * operator discovers they need one, because the picker beside
						 * this text is empty or missing the source they wanted.
						 */}
						<Link className="underline" to="/catalog/sources">
							Manage sources
						</Link>
					</CardDescription>
				</CardHeader>
				<CardContent className="grid gap-4">
					<div className="grid gap-4 md:grid-cols-2">
						<div className="grid gap-2">
							<Label htmlFor="import-source">Source</Label>
							{/*
							 * THE PICKER AND "+ NEW" ON ONE LINE. Discovering you have no
							 * source for the file in your hand is the commonest way this
							 * screen fails, and sending someone to another screen to fix it
							 * loses the file they had chosen.
							 */}
							<div className="flex items-center gap-2">
								<Select value={sourceId} onValueChange={setSourceId}>
									<SelectTrigger id="import-source" aria-label="Source">
										<SelectValue placeholder="Choose a source" />
									</SelectTrigger>
									<SelectContent>
										<SelectGroup>
											{activeSources.map((source) => (
												<SelectItem key={source.id} value={source.id}>
													{source.brandName
														? `${source.brandName} — ${source.name}`
														: source.name}
												</SelectItem>
											))}
										</SelectGroup>
									</SelectContent>
								</Select>
								<Button
									className="border-dashed text-muted-foreground"
									onClick={() => setSourceDialog({ kind: "create" })}
									size="sm"
									type="button"
									variant="outline"
								>
									+ New
								</Button>
							</div>
							<p className="text-xs text-muted-foreground">
								Carries the price basis, the column mapping and the category
								mappings. <span className="font-medium">+ New</span> creates the
								brand too, without leaving this screen.
							</p>
							{sources.isSuccess && activeSources.length === 0 ? (
								<p className="text-xs text-muted-foreground">
									No active sources. A source records which brand a document
									belongs to and what its prices mean.
								</p>
							) : null}
						</div>

						<div className="grid gap-2">
							<Label htmlFor="import-date">Document date</Label>
							<Input
								id="import-date"
								type="date"
								value={effectiveFrom}
								onChange={(event) => setEffectiveFrom(event.target.value)}
							/>
							{/*
							 * Optional, and the label says "document" rather than "today":
							 * a list dated three months ago is already due for a refresh the
							 * moment it is loaded, and stamping it with now would hide that
							 * for a full cadence.
							 */}
							<p className="text-xs text-muted-foreground">
								The date printed on the list. Leave blank for today.
							</p>
						</div>
					</div>

					<div className="grid gap-2">
						<Label htmlFor="import-file">File</Label>
						{/*
						 * A DROP TARGET AROUND THE NATIVE INPUT, not instead of it. The
						 * input stays exactly where it was, so keyboard and screen-reader
						 * operators keep the control they already had and the label keeps
						 * pointing at something real; the region only adds a second way in.
						 */}
						<fieldset
							className={`grid gap-2 rounded-2xl border border-dashed p-3 ${
								dragDepth > 0 ? "border-primary bg-primary/5" : "border-input"
							}`}
							/*
							 * A DEPTH COUNT, not a boolean. `dragenter` and `dragleave` both
							 * BUBBLE, and this fieldset has children -- a legend, the input and
							 * a paragraph, plus the file name once one is chosen -- so crossing
							 * into the input fires a leave for the fieldset immediately
							 * followed by an enter. A boolean flickered off and on for the
							 * whole drag.
							 *
							 * A `contains(relatedTarget)` guard was tried first. It is harder
							 * to test than it looks: jsdom has no `DragEvent`, so Testing
							 * Library falls back to `Event` and `relatedTarget` is absent
							 * rather than null. It is reachable with a hand-built `MouseEvent`,
							 * so this is a preference for the simpler mechanism, not a claim
							 * that the other cannot be tested.
							 */
							onDragEnter={() => setDragDepth((depth) => depth + 1)}
							onDragLeave={() =>
								setDragDepth((depth) => Math.max(0, depth - 1))
							}
							onDragOver={(event) => {
								// Without this no `drop` event fires at all -- the browser takes
								// the dragover as "not a drop target" and navigates to the file.
								// State is the counter's business, not this handler's.
								event.preventDefault();
							}}
							onDrop={(event) => {
								// Suppresses the browser's own open-the-file behaviour AFTER
								// this handler has run. Unlike the `dragover` one, the drop has
								// already reached us by now.
								event.preventDefault();
								setDragDepth(0);
								const dropped = event.dataTransfer.files?.[0];
								/*
								 * A drop with no file reaches here -- `files` is empty when the
								 * drag carried only text -- and `isAcceptedFile` would then read
								 * `.name` off nothing.
								 *
								 * Pinned by watching for the `error` event React reports an
								 * uncaught handler throw through. An earlier version of this
								 * comment called the line unpinnable on the grounds that a
								 * throw changes nothing observable; it reaches `window`.
								 */
								if (!dropped) return;
								if (!isAcceptedFile(dropped)) {
									setDropError(
										"That file is not one this can read. Drop a CSV, an HTML page or an Excel sheet.",
									);
									return;
								}
								setDropError(null);
								setFile(dropped);
							}}
						>
							{/*
							 * A FIELDSET, not a div with `role="group"`: the drag handlers
							 * make this interactive, and `useSemanticElements` asks for the
							 * element that already means what the role says. The legend is
							 * the group's accessible name and is hidden because the visible
							 * label above already names the control inside.
							 */}
							<legend className="sr-only">
								Choose or drop the price list file
							</legend>
							{/*
							 * THE INPUT IS STILL THE CONTROL, only not the thing you look at.
							 * `sr-only` rather than `hidden`, which would take it out of the
							 * tab order and off the accessibility tree -- the label below
							 * points at it, so clicking the prompt opens the picker and a
							 * keyboard reaches it exactly as before.
							 */}
							<Input
								accept={ACCEPTED_FILES}
								className="sr-only"
								id="import-file"
								onChange={(event) => {
									setDropError(null);
									setFile(event.target.files?.[0] ?? null);
								}}
								ref={fileInput}
								type="file"
							/>
							<label
								className="cursor-pointer text-center text-sm"
								htmlFor="import-file"
							>
								<span className="font-medium">Drop a file here</span>{" "}
								<span className="text-muted-foreground">or</span>{" "}
								<span className="underline">browse</span>
							</label>
							<p className="text-center text-xs text-muted-foreground">
								CSV, HTML or Excel &middot; up to 20,000 rows
							</p>
							{/*
							 * INSIDE the zone, as the sketch has it: the template and the
							 * file are one decision -- you are either bringing a file or
							 * going to make one -- and the button was previously a clause
							 * in a paragraph below.
							 */}
							<Button
								className="justify-self-center"
								onClick={downloadTemplate}
								size="sm"
								type="button"
								variant="outline"
							>
								Download the import template
							</Button>
							{/*
							 * The name, because a dropped file does not appear in the native
							 * input's own text -- `input.files` cannot be set from a drop
							 * without a `DataTransfer` dance, so nothing on screen would
							 * otherwise confirm which file is about to be uploaded.
							 */}
							{file ? <p className="text-xs font-medium">{file.name}</p> : null}
						</fieldset>
						{dropError ? (
							/*
							 * Named in words, never `ACCEPTED_FILES` itself. That constant is
							 * six MIME strings and two extensions, which tells an operator
							 * nothing about what to do next.
							 */
							<p className="text-xs text-destructive" role="alert">
								{dropError}
							</p>
						) : null}
						{/*
						 * The four-line explanation that used to sit here is gone. It said
						 * what the template is for, which the button now says by being in
						 * the zone, and what a carried-forward mapping does, which the run
						 * screen's own banner says at the moment it matters. Grey text
						 * nobody reads is not where either belongs.
						 */}
					</div>

					<div>
						<Button
							disabled={!canUpload}
							onClick={() => {
								setActionError(null);
								upload.mutate();
							}}
							size="sm"
						>
							{upload.isPending ? "Staging…" : "Stage file"}
						</Button>
						{actionError ? (
							<p className="mt-2 text-sm text-destructive" role="alert">
								{actionError}
							</p>
						) : null}
					</div>
				</CardContent>
			</Card>

			<Card>
				<CardHeader>
					<CardTitle>Runs</CardTitle>
					<CardDescription>
						{waitingOnly
							? "Runs staged or resolved and not yet committed, newest first."
							: "Every file that has been staged, newest first."}
					</CardDescription>
					<label className="flex items-center gap-2 text-sm">
						<input
							type="checkbox"
							checked={waitingOnly}
							onChange={(event) => {
								setSearchParams(
									event.target.checked ? { status: "waiting" } : {},
								);
							}}
						/>
						Only runs waiting to be committed
					</label>
				</CardHeader>
				<CardContent>
					{runs.isPending ? (
						<div className="grid gap-2">
							<Skeleton className="h-9 w-full" />
							<Skeleton className="h-9 w-full" />
							<Skeleton className="h-9 w-full" />
						</div>
					) : runs.isError ? (
						<div className="grid gap-3 rounded-2xl border border-dashed p-6 text-center">
							<p className="text-sm text-muted-foreground">
								Could not load runs.
							</p>
							<div>
								<Button
									variant="outline"
									size="sm"
									onClick={() => runs.refetch()}
								>
									Try again
								</Button>
							</div>
						</div>
					) : (runs.data ?? []).length === 0 ? (
						offset > 0 ? (
							<EmptyState
								title="No older runs"
								hint="Go back with Newer runs."
							/>
						) : waitingOnly ? (
							<EmptyState
								title="No runs are waiting"
								hint="Every staged run has been committed or discarded."
							/>
						) : (
							<EmptyState
								title="Nothing imported yet"
								hint="Stage a price list above. Nothing reaches the catalogue until you review the dry run and commit it."
							/>
						)
					) : (
						<DataTable
							rows={runs.data ?? []}
							rowKey={(run) => run.id}
							isRefreshing={runs.isFetching && !runs.isPending}
							onRowActivate={(run) => navigate(`/catalog/imports/${run.id}`)}
							rowLabel={(run) => `Open run ${run.fileName ?? run.id}`}
							columns={[
								{
									header: "File",
									cell: (run) => (
										<div className="grid">
											<span className="font-medium">
												{run.fileName ?? "(file removed)"}
											</span>
											<span className="text-xs text-muted-foreground">
												{run.sourceName ?? "(source removed)"}
											</span>
										</div>
									),
								},
								{
									header: "State",
									cell: (run) => <StatusBadge status={run.status} />,
								},
								{
									header: "Rows",
									align: "right",
									cellClassName: "tabular-nums",
									cell: (run) => run.rowsTotal,
								},
								{
									/*
									 * ITS OWN COLUMN, because `counters()` deliberately does not
									 * carry it and this is the number that decides whether a run
									 * can finish at all. Buried in the result string it would read
									 * as one more outcome; it is the only one that is outstanding
									 * WORK, and the run screen cannot be reached from anywhere
									 * else in this table.
									 */
									header: "Needs mapping",
									align: "right",
									cellClassName: "tabular-nums",
									cell: (run) =>
										run.rowsNeedMapping > 0 ? (
											<span className="rounded-full bg-amber-50 px-2 py-0.5 text-xs font-medium text-amber-900">
												{run.rowsNeedMapping}
											</span>
										) : (
											// An em dash, not a zero: a resolved run with none is
											// finished, and a column of zeroes reads as a problem.
											<span className="text-muted-foreground">&mdash;</span>
										),
								},
								{
									header: "Result",
									cell: (run) => (
										<span className="text-sm text-muted-foreground">
											{counters(run)}
										</span>
									),
								},
								{
									header: "Staged",
									cell: (run) =>
										new Date(run.createdAt).toLocaleDateString("en-IN", {
											day: "numeric",
											month: "short",
											year: "numeric",
										}),
								},
							]}
						/>
					)}
					{offset > 0 || (runs.data ?? []).length === IMPORT_RUNS_PAGE ? (
						<div className="mt-3 flex gap-2">
							<Button
								variant="outline"
								size="sm"
								disabled={offset === 0}
								onClick={() =>
									setOffset(Math.max(0, offset - IMPORT_RUNS_PAGE))
								}
							>
								Newer runs
							</Button>
							<Button
								variant="outline"
								size="sm"
								disabled={(runs.data ?? []).length < IMPORT_RUNS_PAGE}
								onClick={() => setOffset(offset + IMPORT_RUNS_PAGE)}
							>
								Older runs
							</Button>
						</div>
					) : null}
				</CardContent>
			</Card>
			{sourceDialog ? (
				<SourceDialog
					mode={sourceDialog}
					onClose={() => setSourceDialog(null)}
					onSaved={() => {
						setSourceDialog(null);
						// The picker is the whole reason this dialog is here, so the list
						// behind it has to carry the new source before it closes.
						queryClient.invalidateQueries({
							queryKey: ["catalog-import-sources"],
						});
					}}
				/>
			) : null}
		</div>
	);
}
